import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import React, { Fragment, useEffect, useState } from "react";
import {
  createNewQuestion,
  fetchEditQuestion,
  fetchQuestionList,
} from "../actions/question";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import DeleteIcon from "@mui/icons-material/Delete";
import InputField from "../components/Admin/Reusable/InputField";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import { basicFormValidationHandler } from "../utils";
import { fetchCountryFormList } from "../actions/country";
import { fetchEducationFormList } from "../actions/education";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import FormSystemInformation from "../components/Admin/Reusable/FormSystemInformation";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";

const QuestionAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  //breadcrumb
  const translateForm = "questionsForm";
  const translateModule = "questions";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  const initialState = {
    name: "",
    position: "",
    educationId: "",
    description: "1",
    answers: "",
    status: "ACTIVE",
  };

  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [answers, setAnswers] = useState([]);
  const [countryValue, setCountryValue] = useState("");

  const { questionDetail } = useSelector((state) => state.questionReducer);
  const { countryFormList } = useSelector((state) => state.countryReducer);
  const { educationFormList } = useSelector((state) => state.educationReducer);

  const handleInputChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleAddAnswer = () => {
    const newAnswer = {
      text: "",
      isCorrect: false,
    };
    setAnswers([...answers, newAnswer]); // Dodavanje novog odgovora u listu
  };

  const handleInputChangeAnswer = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;

    // Ako je korisnik označio jedan odgovor kao tačan, postavimo sve ostale na netačno
    if (field === "isCorrect" && value === true) {
      updatedAnswers.forEach((answer, i) => {
        if (i !== index) {
          answer.isCorrect = false; // Postavljanje svih ostalih odgovora na netačno
        }
      });
    }

    setAnswers(updatedAnswers);
  };

  // Funkcija za uklanjanje odgovora
  const handleRemoveAnswer = (index) => {
    const updatedAnswers = answers.filter((answer, i) => i !== index);
    setAnswers(updatedAnswers);
  };

  useEffect(() => {
    dispatch(fetchCountryFormList());
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
      setAnswers([]);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchQuestionList(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (questionDetail && id) {
      setData({
        ...data,
        name: questionDetail.name || "",
        position: questionDetail.position || "",
        description: questionDetail.description || "1",
        educationId: questionDetail.educationId || "",
        status: questionDetail.status || "ACTIVE",
        createdAt: questionDetail.createdAt || "",
        updatedAt: questionDetail.updatedAt || "",
      });

      if (questionDetail && questionDetail.countryId) {
        setCountryValue(questionDetail.countryId);
        dispatch(fetchEducationFormList(questionDetail.countryId));
      }

      // Učitavanje odgovora
      if (questionDetail.answers) {
        setAnswers(
          questionDetail.answers.map((answer, index) => ({
            text: answer.text,
            isCorrect: answer.isCorrect || false, // Postavi na false ako nije označeno
          }))
        );
      }
    }
  }, [questionDetail, id, dispatch]);

  const clientValidationHandler = () => {
    const { name, position, educationId } = data;

    let errorsObject = {};

    errorsObject = basicFormValidationHandler({
      name,
      position,
      educationId,
      countryValue,
    });

    setErrors(errorsObject);

    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja", { theme: "colored" });
    return !isEmpty(errorsObject);
  };

  //ako je korisnik setovan da je admin drzave odmah se nakon dobijanja drzava proverava da li je count 1 i odmah je setuje
  useEffect(() => {
    if (countryFormList.length == 1 && !countryValue) {
      const singleCountry = countryFormList[0];
      setCountryValue(singleCountry.id);
      dispatch(fetchEducationFormList(singleCountry.id));
    }
  }, [countryFormList, countryValue, dispatch]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (clientValidationHandler()) {
      return;
    }
    data.answers = answers;

    if (answers.length < 2) {
      toast.error("Morate uneti najmanje dva odgovora", { theme: "colored" });
      return;
    }

    let allTextFilled = true;
    let hasCorrectAnswer = false;

    for (let i = 0; i < answers.length; i++) {
      if (!answers[i].text) {
        allTextFilled = false;
        break;
      }
      if (answers[i].isCorrect) {
        hasCorrectAnswer = true;
      }
    }

    if (!allTextFilled) {
      toast.error("Svi odgovori moraju imati tekst", { theme: "colored" });
      return;
    }

    if (!hasCorrectAnswer) {
      toast.error("Morate označiti barem jedan odgovor kao tačan", {
        theme: "colored",
      });
      return;
    }

    if (id) {
      data.id = id;
      dispatch(fetchEditQuestion(data, navigate));
    } else {
      dispatch(createNewQuestion(data, navigate));
    }
  };

  const handleChangeCountry = (event) => {
    setCountryValue(event.target.value);
    dispatch(fetchEducationFormList(event.target.value));
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={translateModule}
        id={id}
        pageType={pageType}
        extraTitle={questionDetail && questionDetail.name}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12} md={12} xs={12} className="">
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="question-holder-admin-left-side-text">
                            <Row>
                              <Col lg={12} md={12} xs={12} className="mb-4">
                                <InputField
                                  label="Naslov pitanja"
                                  name="name"
                                  value={data.name}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <FormControl
                                  fullWidth
                                  size="small"
                                  required
                                  error={errors?.countryValue}
                                  disabled={countryFormList?.length == 1}
                                >
                                  <InputLabel id="demo-simple-select-label">
                                    Država
                                  </InputLabel>
                                  <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={countryValue}
                                    label="Država"
                                    onChange={handleChangeCountry}
                                    size="small"
                                    disabled={id && true}
                                  >
                                    {countryFormList?.map((item, key) => (
                                      <MenuItem value={item.id} key={key}>
                                        {item.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {errors?.countryValue && (
                                    <FormHelperText>
                                      {errors?.countryValue}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Col>
                              <Col lg={6} md={12} xs={12} className="mb-3">
                                <FormControl
                                  fullWidth
                                  required
                                  size="small"
                                  error={errors?.educationId ? true : false}
                                >
                                  <InputLabel
                                    id="demo-simple-select-label"
                                    size="small"
                                  >
                                    Edukacija
                                  </InputLabel>
                                  <Select
                                    value={data.educationId}
                                    label="Edukacija"
                                    name="educationId"
                                    onChange={handleInputChange}
                                    size="small"
                                    disabled={
                                      countryValue === "" ||
                                      educationFormList?.length === 0 ||
                                      (id && true)
                                    }
                                  >
                                    {educationFormList?.map((item, key) => (
                                      <MenuItem value={item.id} key={key}>
                                        {item.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {errors?.educationId && (
                                    <FormHelperText>
                                      obavezno polje
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Col>
                              <Col lg={2} md={12} xs={12} className="mb-4">
                                <InputField
                                  type="number"
                                  label="Pozicija"
                                  name="position"
                                  value={data.position}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                  disabled={id && true}
                                />
                              </Col>
                              {/* <Col lg={12} md={12} xs={12} className="mb-4">
                                <InputField
                                  label="Detalji pitanja"
                                  name="description"
                                  value={data.description}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  multiline
                                  rows={4}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col> */}
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Col lg={12} md={12} xs={12} className="mb-3">
                    <Card>
                      <Card.Header>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="card-title-item">
                            Odgovori na pitanja
                          </div>
                          {!id && (
                            <div className="card-button-header-item">
                              <Button
                                variant="contained"
                                color="info"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddAnswer}
                              >
                                Dodaj novi odgovor
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <Grid container spacing={2} className="mb-3">
                          <Grid item flex={1}>
                            Odgovor
                          </Grid>
                          <Grid item>Tačno</Grid>
                          {!id && <Grid item>Brisanje</Grid>}
                        </Grid>
                        {answers.map((answer, index) => (
                          <Grid
                            container
                            spacing={2}
                            key={answer.id}
                            className="mb-3"
                          >
                            <Grid item flex={1}>
                              <InputField
                                type="text"
                                size="small"
                                disabled={!!id && true}
                                value={answer.text}
                                onChange={(e) =>
                                  handleInputChangeAnswer(
                                    index,
                                    "text",
                                    e.target.value
                                  )
                                }
                                placeholder="Unesite odgovor"
                                className="form-control"
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <FormControl
                                component="fieldset"
                                disabled={!!id && true}
                              >
                                <RadioGroup
                                  row
                                  value={answer.isCorrect ? "tacno" : "netacno"}
                                  onChange={(e) =>
                                    handleInputChangeAnswer(
                                      index,
                                      "isCorrect",
                                      e.target.value === "tacno"
                                    )
                                  }
                                >
                                  <FormControlLabel
                                    value="tacno"
                                    control={<Radio />}
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            {!id && (
                              <Grid item>
                                <div
                                  onClick={() => handleRemoveAnswer(index)}
                                  className="d-flex align-items-center justify-content-center h-100 remove-answer-icon"
                                >
                                  <DeleteIcon />
                                </div>
                              </Grid>
                            )}
                          </Grid>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <SelectStatus
                        data={data}
                        disabled={id && true}
                        handleInputChange={handleInputChange}
                      />
                    </Col>

                    {id && (
                      <Col lg={12}>
                        <FormSystemInformation data={data} />
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default QuestionAdd;
